import { Controller, FormProvider, useForm } from "react-hook-form";
import { useEffect } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import FormFooterEdit from "components/Forms/FooterEdit";
import FormField from "ds/components/Form/Field";
import Select from "ds/components/Select";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import useTypedContext from "hooks/useTypedContext";
import { makeScanUpdateInput } from "shared/Scan/helpers";
import useScanFormOptions from "shared/Scan/useScanFormOptions";
import useScanUpdate from "shared/Scan/useScanUpdate";

import { ScanSettingsContextData } from "../Context";
import { REFETCH_SCAN_AND_SCAN_SETTINGS_QUERIES } from "../constants";
import { getFormDefaultValues, ScanSettingsWorkerPoolFormFields } from "./helpers";

const ScanSettingsWorkerPoolEdit = () => {
  const { scanSettings } = useTypedContext(ScanSettingsContextData);
  const { reportSuccess } = useTypedContext(FlashContext);

  const [updateScan, { loading }] = useScanUpdate({
    refetchQueries: REFETCH_SCAN_AND_SCAN_SETTINGS_QUERIES,
  });

  const {
    workerPools,
    loading: formOptionsLoading,
    error: formOptionsError,
  } = useScanFormOptions(scanSettings.space.id);

  const form = useForm<ScanSettingsWorkerPoolFormFields>({
    defaultValues: getFormDefaultValues(scanSettings),
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
    reset,
    setValue,
    watch,
  } = form;

  useBlockNavigationModal({ isNavigationBlocked: isDirty });

  const onSubmit = (formData: ScanSettingsWorkerPoolFormFields) => {
    updateScan(
      { id: scanSettings.id, input: makeScanUpdateInput(scanSettings, formData) },
      (data) => {
        if (data?.scanUpdate) {
          reportSuccess({ message: "Scan was successfully updated" });
        }
      }
    );
  };

  useEffect(() => {
    reset(getFormDefaultValues(scanSettings));
  }, [reset, scanSettings]);

  const selectedWorkerPoolId = watch("workerPoolId");

  // Clears the field once the currently selected worker pool is no longer attachable in this
  // space (e.g. it moved spaces) — resubmitting a stale value is rejected server-side, so an
  // emptied field just falls back to the existing required validation below instead of silently
  // failing the save.
  useEffect(() => {
    if (formOptionsLoading || formOptionsError) {
      return;
    }

    if (
      selectedWorkerPoolId &&
      !workerPools.some((workerPool) => workerPool.id === selectedWorkerPoolId)
    ) {
      setValue("workerPoolId", "");
    }
  }, [formOptionsLoading, formOptionsError, workerPools, selectedWorkerPoolId, setValue]);

  const workerPoolOptions = workerPools.map(({ id, name }) => ({ value: id, label: name }));

  return (
    <FormProvider {...form}>
      <Controller
        name="workerPoolId"
        control={control}
        rules={{ required: "Worker pool is required." }}
        render={({ field }) => (
          <FormField noMargin label="Worker pool" error={errors?.workerPoolId?.message}>
            {({ ariaInputProps }) => (
              <Select
                value={field.value}
                items={workerPoolOptions}
                isLoading={formOptionsLoading}
                onChange={field.onChange}
                placeholder="Please select"
                isError={!!errors?.workerPoolId}
                {...ariaInputProps}
              />
            )}
          </FormField>
        )}
      />

      <FormFooterEdit
        onSubmit={handleSubmit(onSubmit)}
        onCancel={() => reset(getFormDefaultValues(scanSettings))}
        isDirty={isDirty}
        submitLoading={loading}
      />
    </FormProvider>
  );
};

export default ScanSettingsWorkerPoolEdit;
